import { Result } from "@/api/types";
import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { dateFormat, useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { filterSearchToGetFilter, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { IImpact, ImpactMetricKey } from "../utils/types";

interface GetProductImpactTopContributors extends Result {
  result: {
    top_contributors: {
      id: number;
      external_id: string;
      product_name: string;
      impact: IImpact;
    }[];
  };
}

export type ProductBy = "single" | "total";
export const PRODUCT_BY: { value: ProductBy; label: string }[] = [
  { value: "total", label: "Total" },
  { value: "single", label: "Single" },
];

export const getProductNameOrId = (product: { product_name: string; external_id: string }) => {
  return product.product_name.trim() || product.external_id;
};

export function useProductImpactTopContributors({
  by,
  page,
  pageSize,
  metric,
}: {
  by: ProductBy;
  page: number;
  pageSize: number;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: [
      "dashboard",
      "product",
      "productImpactTopContributors",
      by,
      page,
      pageSize,
      startDate,
      endDate,
      metric,
    ],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetProductImpactTopContributors>("/dashboard/products/top_contributors", {
          params: {
            page,
            page_size: pageSize,
            by,
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            order_by: metric,
            metric,
          },
        })
        .then((result) => ({
          ...result.data.result,
          top_contributors: result.data.result.top_contributors.map((product) => ({
            ...product,
            label: getProductNameOrId(product),
          })),
        })),
  });

  return query;
}

export interface SingleProductImpactDetailPerLifeCycle {
  id: number;
  label: string;
  external_id: string;
  product_name: string;
  quantity: number;
  impact: LifecycleImpact;
}

export interface ProductImpactDetailPerLifeCycle {
  total_rows: number;
  total_impact: LifecycleImpact;
  data: SingleProductImpactDetailPerLifeCycle[];
}

export interface GetProductImpactDetailPerLifecycle extends Result {
  result: ProductImpactDetailPerLifeCycle;
}

export function useProductImpactDetail({
  by,
  pagination,
  filter,
  search,
  sort,
  metric,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  by: ProductBy;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: [
      "dashboard",
      "product",
      "productImpactDetailPerLifecycle",
      startDate,
      endDate,
      by,
      pagination,
      filter,
      search,
      sort,
      metric,
    ],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetProductImpactDetailPerLifecycle>("/dashboard/products/details_per_life_cycle", {
          params: {
            by,
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            metric,
          },
        })
        .then((result) => ({
          ...result.data.result,
          data: result.data.result.data.map((product) => ({
            ...product,
            label: getProductNameOrId(product),
          })),
        })),
  });

  return query;
}

export type Lifecycle =
  | "material"
  | "production"
  | "transport"
  | "product"
  | "eol"
  | "usage"
  | "packaging";

export type LifecycleImpact = Record<Lifecycle, IImpact>;

export interface ProductCategoryImpactDetail {
  category?: string;
  quantity: number;
  impact: LifecycleImpact;
}
interface GetProductImpactDetailsPerCategory {
  data: ProductCategoryImpactDetail[];
  total_impact: LifecycleImpact;
  total_rows: number;
}

export function useProductImpactCategories({
  by,
  pagination,
  filter,
  search,
  sort,
  metric,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  by: ProductBy;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: [
      "dashboard",
      "product",
      "CATEGORIES",
      startDate,
      endDate,
      by,
      pagination,
      filter,
      search,
      sort,
      metric,
    ],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetProductImpactDetailsPerCategory>("/dashboard/products/details_per_category", {
          params: {
            by,
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            metric,
          },
        })
        .then((data) => data.data),
  });

  return query;
}

export function useProductImpactDetailExport({
  pagination,
  filter,
  search,
  sort,
  by,
  metric,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  by: ProductBy;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();

  return useMutation({
    mutationFn: () =>
      axios
        .get<Result<{ url: string }>>("/dashboard/products/details_per_life_cycle", {
          params: {
            by,
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            export: true,
            metric,
          },
        })
        .then((result) => result.data.result.url),
  });
}

interface GetProductImpactDetailPerCategory extends Result {
  result: {
    total_rows: number;
    data: {
      external_id: string;
      product_name: string;
      quantity: number;
      environmental_cost: number;
    }[];
    total_impact: number;
  };
}

export function useProductImpactDetailPerCategory({
  by,
  pagination,
  filter,
  search,
  sort,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  by: ProductBy;
}) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: [
      "dashboard",
      "product",
      "productImpactDetailPerCategory",
      startDate,
      endDate,
      by,
      pagination,
      filter,
      search,
      sort,
    ],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetProductImpactDetailPerCategory>("/dashboard/products/details_per_impact_category", {
          params: {
            by,
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
          },
        })
        .then((result) => ({
          ...result.data.result,
          data: result.data.result.data.map((product) => ({
            ...product,
            label: getProductNameOrId(product),
          })),
        })),
  });

  return query;
}

export function useProductImpactDetailPerCategoryExport({
  pagination,
  filter,
  search,
  sort,
  by,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  by: ProductBy;
}) {
  const { startDate, endDate } = useDateRange();

  return useMutation({
    mutationFn: () =>
      axios
        .get<Result<{ url: string }>>("/dashboard/products/details_per_impact_category", {
          params: {
            by,
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            export: true,
          },
        })
        .then((result) => result.data.result.url),
  });
}

export function useProductImpactDetailPerScopeExport({
  pagination,
  filter,
  search,
  sort,
  by,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  by: ProductBy;
}) {
  const { startDate, endDate } = useDateRange();

  return useMutation({
    mutationFn: () =>
      axios
        .get<Result<{ url: string }>>("/dashboard/products/details_per_impact_scope", {
          params: {
            by,
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            export: true,
          },
        })
        .then((result) => result.data.result.url),
  });
}

interface GetProductImpactTotals extends Result {
  result: {
    data: { label: string; impact: IImpact }[];
    total_impact: IImpact;
  };
}

export function useProductImpactTotals({
  by = "total",
  metrics,
}: {
  by?: ProductBy;
  metrics: ImpactMetricKey[];
}) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: ["dashboard", "product", "productImpactPerCategory", startDate, endDate, by, metrics],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetProductImpactTotals>("/dashboard/products/totals", {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            by,
            metrics: metrics.join(";"),
          },
        })
        .then((result) => result.data.result),
  });

  return query;
}

export interface GetProductImpactTotalsPerLifecyclePerProduct {
  id: number;
  external_id: string;
  product_name: string;
  impact: { label: string; impact: IImpact }[];
  total_impact: IImpact;
}

export function useProductImpactTotalsPerLifecyclePerProduct({
  productId,
  by,
  metrics,
}: {
  productId: number;
  by: ProductBy;
  metrics: ImpactMetricKey[];
}) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: [
      "products",
      "detail",
      productId,
      "impact",
      "lifecycle",
      startDate,
      endDate,
      by,
      metrics,
    ],
    enabled: !!startDate && !!endDate && !!productId,
    queryFn: () =>
      axios
        .get<GetProductImpactTotalsPerLifecyclePerProduct>(
          `/products/${productId}/impact_per_lifecycle`,
          {
            params: {
              end_date: endDate?.format(dateFormat),
              start_date: startDate?.format(dateFormat),
              by,
              metrics: metrics.join(";"),
            },
          }
        )
        .then((result) => result.data),
  });

  return query;
}

export interface MaterialImpactPerProductMaterial {
  rowId: string;
  id: number;
  name: string;
  unit: string;
  quantity: number;
  impact: IImpact;
}

export interface GetProductMaterialImpactPerMaterial {
  materials: MaterialImpactPerProductMaterial[];
  total_impact: IImpact;
  total_count: number;
}

export function useProductMaterialImpactPerMaterial({
  pagination,
  filter,
  search,
  sort,
  productId,
  by,
  metric,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  productId: number;
  by: ProductBy;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: [
      "products",
      "detail",
      productId,
      "impact",
      "material",
      pagination,
      filter,
      search,
      sort,
      startDate,
      endDate,
      by,
      metric,
    ],
    enabled: !!startDate && !!endDate && !!productId,
    queryFn: () =>
      axios
        .get<GetProductMaterialImpactPerMaterial>(
          `/products/${productId}/material_impact_per_material`,
          {
            params: {
              by,
              metric,
              end_date: endDate?.format(dateFormat),
              start_date: startDate?.format(dateFormat),
              page: pagination?.page === undefined ? 1 : pagination.page,
              page_size: pagination?.pageSize || 50,
              ...filterSearchToGetFilter(filter, search),
              ...sortToParam(sort),
            },
          }
        )
        .then((result) => ({
          ...result.data,
          materials: result.data.materials.map((x) => ({ ...x, rowId: x.id + x.unit })),
        })),
  });

  return query;
}

export interface GetProductTransportImpactPerMode {
  travel_mode: string;
  distance: number;
  impact: IImpact | null;
}

export function useProductTransportImpactPerMode({
  productId,
  by,
  metric,
}: {
  productId: number;
  by: ProductBy;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();
  const query = useQuery({
    queryKey: [
      "products",
      "detail",
      productId,
      "impact",
      "transport",
      "per_mode",
      startDate,
      endDate,
      by,
      metric,
    ],
    enabled: !!startDate && !!endDate && !!productId,
    queryFn: () =>
      axios
        .get<GetProductTransportImpactPerMode[]>(
          `/products/${productId}/transport_impact_per_mode`,
          {
            params: {
              end_date: endDate?.format(dateFormat),
              start_date: startDate?.format(dateFormat),
              by,
              metric,
            },
          }
        )
        .then((result) => result.data),
  });

  return query;
}

export interface GetProductTransportImpactPerType {
  label: "inbound_transport" | "outbound_transport";
  impact: IImpact | null;
}

export function useProductTransportImpactPerType({
  productId,
  by,
  metric,
}: {
  productId: number;
  by: ProductBy;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();
  const query = useQuery({
    queryKey: [
      "products",
      "detail",
      productId,
      "impact",
      "transport",
      "per_type",
      startDate,
      endDate,
      by,
      metric,
    ],
    enabled: !!startDate && !!endDate && !!productId,
    queryFn: () =>
      axios
        .get<GetProductTransportImpactPerType[]>(
          `/products/${productId}/transport_impact_per_type`,
          {
            params: {
              end_date: endDate?.format(dateFormat),
              start_date: startDate?.format(dateFormat),
              by,
              metric,
            },
          }
        )
        .then((result) => result.data),
  });

  return query;
}

export interface GetProductProductionImpactPerSource {
  name: string;
  amount: number;
  energy_type: string;
  unit: string;
  impact: IImpact;
}

export function useProductProductionImpactPerSource({
  productId,
  by,
  metric,
}: {
  productId: number;
  by: ProductBy;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();
  const query = useQuery({
    queryKey: [
      "product",
      productId,
      "impact",
      "production",
      "per_source",
      startDate,
      endDate,
      by,
      metric,
    ],
    enabled: !!startDate && !!endDate && !!productId,
    queryFn: () =>
      axios
        .get<GetProductProductionImpactPerSource[]>(
          `/products/${productId}/production_impact_per_source`,
          {
            params: {
              end_date: endDate?.format(dateFormat),
              start_date: startDate?.format(dateFormat),
              by,
              metric,
            },
          }
        )
        .then((result) => result.data),
  });

  return query;
}

export interface GetProductProductionImpactPerType {
  energy_type: string;
  impact: IImpact;
}

export function useProductProductionImpactPerType({
  productId,
  by,
  metric,
}: {
  productId: number;
  by: ProductBy;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();
  const query = useQuery({
    queryKey: [
      "product",
      productId,
      "impact",
      "production",
      "per_type",
      startDate,
      endDate,
      by,
      metric,
    ],
    enabled: !!startDate && !!endDate && !!productId,
    queryFn: () =>
      axios
        .get<GetProductProductionImpactPerType[]>(
          `/products/${productId}/production_impact_per_type`,
          {
            params: {
              end_date: endDate?.format(dateFormat),
              start_date: startDate?.format(dateFormat),
              by,
              metric,
            },
          }
        )
        .then((result) => result.data),
  });

  return query;
}

interface getEolImpact {
  name: string | null;
  product_id: string;
  impact: IImpact;
}

export function useProductEolImpact({
  productId,
  by,
  metric,
}: {
  productId: number;
  by: ProductBy;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();
  const query = useQuery({
    queryKey: ["product", productId, "impact", "eol", "per_type", startDate, endDate, by, metric],
    enabled: !!startDate && !!endDate && !!productId,
    queryFn: () =>
      axios
        .get<getEolImpact>(`/products/${productId}/eol_impact`, {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            by,
            metric,
          },
        })
        .then((result) => result.data),
  });

  return query;
}

interface usageImpactPerType {
  type: "electricity" | "consumable";
  label: string;
  unit: string;
  amount: number;
  impact: IImpact;
}

interface getUsageImpact {
  product_id: string;
  impact_per_type: usageImpactPerType[];
  total_impact: IImpact;
}

export function useProductUsageImpactPerType({
  productId,
  by,
  metric,
}: {
  productId: number;
  by: ProductBy;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();
  const query = useQuery({
    queryKey: ["product", productId, "impact", "usage", "per_type", startDate, endDate, by, metric],
    enabled: !!startDate && !!endDate && !!productId,
    queryFn: () =>
      axios
        .get<getUsageImpact>(`/products/${productId}/usage_impact`, {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            by,
            metric,
          },
        })
        .then((result) => result.data),
  });

  return query;
}

interface getUsageImpactCompare {
  product_id: string;
  electricity: IImpact;
  consumables: IImpact;
}

export function useProductUsageImpactCompare({
  productId,
  by,
  metric,
}: {
  productId: number;
  by: ProductBy;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();
  const query = useQuery({
    queryKey: ["product", productId, "impact", "usage", "compare", startDate, endDate, by, metric],
    enabled: !!startDate && !!endDate && !!productId,
    queryFn: () =>
      axios
        .get<getUsageImpactCompare>(`/products/${productId}/usage_impact_compare`, {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            by,
            metric,
          },
        })
        .then((result) => result.data),
  });

  return query;
}

export interface GetTopContributingPackagingMaterials {
  material_id: number;
  material_name: string;
  amount: number;
  unit: string;
  impact: IImpact;
}

export function useTopContributingPackagingMaterials({
  productId,
  by,
  metric,
}: {
  productId: number;
  by: ProductBy;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();
  const query = useQuery({
    queryKey: [
      "products",
      "detail",
      productId,
      "impact",
      "packaging_material",
      "per_mode",
      startDate,
      endDate,
      by,
      metric,
    ],
    enabled: !!startDate && !!endDate && !!productId,
    queryFn: () =>
      axios
        .get<GetTopContributingPackagingMaterials[]>(
          `/products/${productId}/top_contributing_packaging_materials`,
          {
            params: {
              end_date: endDate?.format(dateFormat),
              start_date: startDate?.format(dateFormat),
              by,
              metric,
            },
          }
        )
        .then((result) => result.data),
  });

  return query;
}

export interface GetPackagingImpactPerType {
  label: "Product packaging" | "Logistics packaging";
  impact: IImpact | null;
}

export function usePackagingImpactPerType({
  productId,
  by,
  metric,
}: {
  productId: number;
  by: ProductBy;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();
  const query = useQuery({
    queryKey: [
      "products",
      "detail",
      productId,
      "impact",
      "packaging",
      "per_type",
      startDate,
      endDate,
      by,
      metric,
    ],
    enabled: !!startDate && !!endDate && !!productId,
    queryFn: () =>
      axios
        .get<GetPackagingImpactPerType[]>(
          `/products/${productId}/packaging_products_vs_logistics`,
          {
            params: {
              end_date: endDate?.format(dateFormat),
              start_date: startDate?.format(dateFormat),
              by,
              metric,
            },
          }
        )
        .then((result) => result.data),
  });

  return query;
}
